import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { installErrorReporter } from "@/lib/errorReporter";

// Installed before anything else runs, so a crash during bootstrap — the worst
// kind, because the user sees nothing at all — is still reported. Render
// crashes are caught separately by ErrorBoundary.
installErrorReporter();

// Capture ?ref= and ?invite= at bootstrap, before any router or middleware can strip them.
try {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");
  const existing = sessionStorage.getItem("ref_code");
  if (ref && /^[A-Z0-9]{6,12}$/.test(ref)) {
    sessionStorage.setItem("ref_code", ref);
  }
  const invite = params.get("invite");
  if (invite && /^[A-Z0-9]{6,12}$/.test(invite.toUpperCase())) {
    sessionStorage.setItem("collective_invite", invite.toUpperCase());
  }
  // Runtime footprint so we can diagnose stripping in the wild.
  // eslint-disable-next-line no-console
  console.log("[ref-snapshot]", { search: window.location.search, urlRef: ref, stored: sessionStorage.getItem("ref_code"), preExisting: existing, invite: sessionStorage.getItem("collective_invite") });
} catch (_) { /* ignore */ }

// Item 8 (2026-07): mount-time failures must never be silent — log every
// uncaught error / unhandled rejection so a white page always leaves a trace.
window.addEventListener("error", (e) => {
  // eslint-disable-next-line no-console
  console.error("[window.onerror]", e.message, e.filename, e.lineno);
});
window.addEventListener("unhandledrejection", (e) => {
  // eslint-disable-next-line no-console
  console.error("[unhandledrejection]", e.reason);
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
